import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusPill from "@/components/StatusPill";
import GoogleAddressInput from "@/components/GoogleAddressInput";
import { fmtMoney, fmtDate, titleCase } from "@/lib/format";
import { Plus, Package } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["scheduled", "in_route", "completed", "cancelled"];
const TYPES = ["delivery", "pickup", "swap", "relocation", "final_pull", "emergency"];

export default function Rolloff() {
    const [jobs, setJobs] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [openNew, setOpenNew] = useState(false);

    const load = async () => {
        const [j, c] = await Promise.all([api.get("/rolloff"), api.get("/customers")]);
        setJobs(j.data); setCustomers(c.data);
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/rolloff/${id}`, { status });
            load();
            toast.success(`Job ${status.replace(/_/g, " ")}`);
        } catch { toast.error("Failed"); }
    };

    const byStatus = (s) => jobs.filter((j) => j.status === s);

    return (
        <div data-testid="rolloff-page">
            <PageHeader
                title="Roll-Off Dispatch"
                subtitle="Deliveries, swaps, pickups, final pulls. Drag jobs across the board as your day unfolds."
                actions={<NewJobSheet open={openNew} setOpen={setOpenNew} customers={customers} onSaved={() => { setOpenNew(false); load(); }} />}
            />
            <div className="px-8 py-6">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="rolloff-kanban">
                    {STATUSES.map((s) => (
                        <div key={s}>
                            <div className="flex items-center justify-between mb-3 px-1">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-primary" />
                                    <div className="overline">{titleCase(s)}</div>
                                </div>
                                <div className="font-mono text-xs text-muted-foreground">{byStatus(s).length}</div>
                            </div>
                            <div className="space-y-2 min-h-[300px]">
                                {byStatus(s).map((j) => (
                                    <Card key={j.id} data-testid={`job-card-${j.id}`} className="hover:shadow-sm transition-shadow">
                                        <CardContent className="p-4 space-y-2.5">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="font-medium text-sm truncate">{j.customer_name}</div>
                                                    <div className="text-xs text-muted-foreground font-mono">{j.job_number}</div>
                                                </div>
                                                <StatusPill status={j.status} />
                                            </div>
                                            <div className="flex items-center gap-2 text-xs">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-secondary">
                                                    <Package className="w-3 h-3" /> {j.container_size}
                                                </span>
                                                <span className="text-muted-foreground">{titleCase(j.job_type)}</span>
                                            </div>
                                            <div className="text-[11px] text-muted-foreground truncate">{j.address}</div>
                                            <div className="flex items-center justify-between pt-1">
                                                <div className="text-xs">{fmtDate(j.scheduled_date)} · <span className="tabular font-semibold">{fmtMoney(j.price)}</span></div>
                                                <Select value={j.status} onValueChange={(v) => updateStatus(j.id, v)}>
                                                    <SelectTrigger className="h-7 text-xs w-28" data-testid={`status-select-${j.id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {STATUSES.map((st) => <SelectItem key={st} value={st}>{titleCase(st)}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function NewJobSheet({ open, setOpen, customers, onSaved }) {
    const [form, setForm] = useState({
        customer_id: "", job_type: "delivery", container_size: "20yd",
        scheduled_date: new Date().toISOString().slice(0, 10), address: "", price: 425,
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async () => {
        const cust = customers.find((c) => c.id === form.customer_id);
        if (!cust) return toast.error("Select a customer");
        try {
            await api.post("/rolloff", {
                customer_id: form.customer_id, job_type: form.job_type,
                container_size: form.container_size,
                scheduled_date: new Date(form.scheduled_date).toISOString(),
                address: form.address || `${cust.address.line1}, ${cust.address.city}`,
                price: parseFloat(form.price),
            });
            toast.success("Job scheduled");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button size="sm" data-testid="new-rolloff-btn"><Plus className="w-4 h-4 mr-1" />New job</Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-lg" data-testid="new-rolloff-sheet">
                <SheetHeader><SheetTitle className="font-heading text-2xl">New roll-off job</SheetTitle>
                    <SheetDescription className="sr-only">Schedule a roll-off delivery, pickup, swap or relocation.</SheetDescription>
                </SheetHeader>
                <div className="space-y-3 mt-6">
                    <Fld label="Customer">
                        <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                            <SelectTrigger data-testid="new-rolloff-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                            <SelectContent>
                                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </Fld>
                    <div className="grid grid-cols-2 gap-3">
                        <Fld label="Type">
                            <Select value={form.job_type} onValueChange={(v) => set("job_type", v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{titleCase(t)}</SelectItem>)}</SelectContent>
                            </Select>
                        </Fld>
                        <Fld label="Container">
                            <Select value={form.container_size} onValueChange={(v) => set("container_size", v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>{["10yd", "20yd", "30yd", "40yd"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </Fld>
                    </div>
                    <Fld label="Scheduled date"><Input type="date" value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} /></Fld>
                    <Fld label="Address override">
                        <GoogleAddressInput
                            value={form.address}
                            onChange={(v) => set("address", v)}
                            onAddressSelected={(a) => set(
                                "address",
                                `${a.line1 || ""}, ${a.city || ""}, ${a.state || ""} ${a.zip || ""}`
                                    .replace(/,\s*,/g, ",").trim().replace(/^,\s*/, "").replace(/,\s*$/, ""))}
                            placeholder="Leave blank to use customer address"
                            testid="rolloff-address"
                        />
                    </Fld>
                    <Fld label="Price"><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} /></Fld>
                    <Button onClick={submit} className="w-full mt-4" data-testid="new-rolloff-submit">Schedule job</Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function Fld({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>; }
